import {
  isDefinedArray,
  isDefinedString,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import { memoize } from "lodash";
import { observable, runInAction } from "mobx";

import { apiCallGetInvestigationCases } from "../../api/investigation-cases-api/api-call-get-investigation-cases";
import { apiCallUpdateInvestigationCase } from "../../api/investigation-cases-api/api-call-update-investigation-case";
import { TEventName, TInvestigationCases } from "../../Types";
import getCleanPoolStore from "../core/core-clean-pool";
import getCorePopUpStore from "../core/core-popup-store";
import getCoreUIStore from "../core/core-ui-store";
import getDashBoardStore from "../dashboard-store";
import { INVESTIGATION_CASES_LIMIT } from "../../constants/input-entries-constants";
import { apiCallGetInvestigationCaseByID } from "../../api/investigation-cases-api/api-call-get-investigation-case-by-id";
import queryString from "query-string";
import getCoreHistoryStore from "../core/core-history-store";
import getUserAdvancedStore from "../user-store/advanced-store";
import { apiCallUpdateUserStatusAndInvestigationCase } from "../../api/investigation-cases-api/api-call-update-investigation-case-and-status";
import { TEt3PickerSingleValue } from "../../TypesNEW";

class InvestigationCasesStore {
  investigationCases = observable.box<TInvestigationCases[]>([]);
  investigationCasesLimit = observable.box<number>(INVESTIGATION_CASES_LIMIT);
  createdFromType = observable.box<TEt3PickerSingleValue>({
    label: "Other",
    value: "other"
  });

  getInvestigationCasesData = () => {
    const dataArray = this.investigationCases.get().map((data) => {
      const investigationCaseResolve = {
        invResolveInvestigationId: data.id,
        invResolveUserUUID: data.userUUID,
        invResolveReasons: data.reasons
      };
      return {
        resolve: investigationCaseResolve,
        CSR: data.CSR,
        TBW_ids: data.TBW_ids,
        action_id: data.action_id,
        action_place: data.action_place,
        action_type: data.action_type,
        created_on: data.created_on,
        gender: data.gender,
        id: data.id,
        investigation_cases_reasons: data.reasons,
        user_id: data.user_id,
        userUUID: data.userUUID,
        isUserReported: data.userUnderCSRReport
      };
    });
    return dataArray;
  };

  setInvestigationCases = (data: TInvestigationCases[]) => {
    runInAction(() => {
      this.investigationCases.set(data);
    });
  };

  setInvestigationCaseCreatedFromType = (status: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.createdFromType.set(status);
    });
  };

  getInvestigationCaseByID = async ({ userID }: { userID: number }) => {
    getCoreUIStore().setIsLoading("INVESTIGATION_CASES", true);
    const { data, status, code } = await apiCallGetInvestigationCaseByID({
      userID: userID
    });
    getCoreUIStore().setIsLoading("INVESTIGATION_CASES", false);

    if (status === "OK") {
      const pool = reduce(() => data.pool, "");

      if (isDefinedString(pool)) {
        getUserAdvancedStore().setAdminPoolTable(pool);
        getCorePopUpStore().openConfirmationPopUps(
          "CLEAN_POOL_CONFIRMATION_POPUP"
        );
      } else {
        getCoreHistoryStore().openNewTab(`/investigationCases?id=${userID}`);
      }
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getData = async ({ withURLSearch }: { withURLSearch: boolean }) => {
    this.setInvestigationCases([]);
    getCoreUIStore().setIsLoading("INVESTIGATION_CASES", true);
    const createdFromType = this.createdFromType.get().value;
    const limit = this.investigationCasesLimit.get();
    const serverResponse = await apiCallGetInvestigationCases({
      createdFromType,
      limit
    });

    const status = reduce(() => serverResponse.status, "ERROR");
    getCoreUIStore().setIsLoading("INVESTIGATION_CASES", false);
    if (status === "ERROR") {
      return getCorePopUpStore().showAlertMessage(serverResponse.code);
    } else {
      const data = reduce(() => serverResponse.data, []);

      if (withURLSearch) {
        const userCreatedFrom = data[0].created_from;
        const isIndonesianOrMalaysian =
          userCreatedFrom === "MY" || userCreatedFrom === "ID";
        !isIndonesianOrMalaysian
          ? this.setInvestigationCaseCreatedFromType({
              label: "Other",
              value: "Other"
            })
          : this.setInvestigationCaseCreatedFromType({
              label: "Indonesia/Malaysia",
              value: "indonesia_malaysia"
            });
      }

      if (isDefinedArray(data) && !isDefinedString(data)) {
        this.setInvestigationCases(data);
      }
    }
  };
  updateInvestigationCase = async ({
    investigationCaseID,
    eventName,
    fromPage,
    userUUID,
    extraData
  }: {
    investigationCaseID: number;
    eventName: "RESOLVE_INVESTIGATION_CASE";
    fromPage: "INVESTIGATION_CASES";
    userUUID: string;
    extraData: string[];
  }) => {
    const { status, code } = await apiCallUpdateInvestigationCase({
      investigationCaseID,
      eventName,
      fromPage,
      userUUID,
      extraData
    });
    if (status === "ERROR") {
      if (code === "NO_LONGER_ACTIVE_POOL") {
        await getCleanPoolStore().automaticCleanPool({
          type: "INVESTIGATION_CASES"
        });
      }
      if (code === "INVESTIGATION_CASE_ALREADY_RESOLVED") {
        const newInvestigationCases = this.investigationCases
          .get()
          .filter((item) => item.id !== investigationCaseID);
        this.setInvestigationCases(newInvestigationCases);
        this.decreaseDashboardInvestigationCasesCount();
      }
      getCorePopUpStore().showAlertMessage(code);
      return;
    } else {
      const newInvestigationCases = this.investigationCases
        .get()
        .filter((item) => item.id !== investigationCaseID);
      this.setInvestigationCases(newInvestigationCases);
      this.decreaseDashboardInvestigationCasesCount();
    }
  };
  updateStatusAndInvestigationCase = async ({
    investigationCaseID,
    eventName,
    fromPage,
    userUUID,
    extraData
  }: {
    investigationCaseID: number;
    eventName: TEventName;
    fromPage: "INVESTIGATION_CASES";
    userUUID: string;
    extraData: {
      pageName: "INVESTIGATION_CASES";
      TBW_ids: number[];
      temporaryBanDays: number;
    };
  }) => {
    const { status, code } = await apiCallUpdateUserStatusAndInvestigationCase({
      investigationCaseID,
      eventName,
      fromPage,
      userUUID,
      extraData
    });
    if (status === "ERROR") {
      if (code === "NO_LONGER_ACTIVE_POOL") {
        await getCleanPoolStore().automaticCleanPool({
          type: "INVESTIGATION_CASES"
        });
      }
      if (code === "INVESTIGATION_CASE_ALREADY_RESOLVED") {
        const newInvestigationCases = this.investigationCases
          .get()
          .filter((item) => item.id !== investigationCaseID);
        this.setInvestigationCases(newInvestigationCases);
        this.decreaseDashboardInvestigationCasesCount();
      }
      getCorePopUpStore().showAlertMessage(code);
      return status;
    } else {
      const newInvestigationCases = this.investigationCases
        .get()
        .filter((item) => item.id !== investigationCaseID);
      this.setInvestigationCases(newInvestigationCases);
      this.decreaseDashboardInvestigationCasesCount();
      return status;
    }
  };
  decreaseDashboardInvestigationCasesCount = () => {
    const investigationCasesCount =
      getDashBoardStore().investigationCasesCount.get();
    if (investigationCasesCount > 0) {
      getDashBoardStore().investigationCasesCount.set(
        investigationCasesCount - 1
      );

      sessionStorage.setItem(
        "counts",
        JSON.stringify({
          reportsCount: reduce(() => getDashBoardStore().reportsCount.get(), 0),
          reportedCount: reduce(
            () => getDashBoardStore().reportedCount.get(),
            0
          ),
          approvalCount: reduce(
            () => getDashBoardStore().approvalCount.get(),
            0
          ),
          investigationCasesCount: reduce(
            () => getDashBoardStore().investigationCasesCount.get() - 1,
            0
          ),
          activeTime: reduce(() => getDashBoardStore().activeTime.get(), 0)
        })
      );
    }
  };

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const searchID = queryString.parse(search).id;
      if (
        isDefinedString(searchID) &&
        isPositiveNumber(parseInt(searchID, 10))
      ) {
        await this.getData({ withURLSearch: true });
      }
    }
  };

  cleanPool = async () => {
    await getCleanPoolStore().cleanPool({
      currentData: this.investigationCases,
      page: "INVESTIGATION_CASES",
      poolType: "INVESTIGATION_CASES",
      successVoidFunction: () => this.setInvestigationCases([])
    });
  };
}

const getInvestigationCasesStore = memoize(
  () => new InvestigationCasesStore(),
  () => 1
);
export default getInvestigationCasesStore;
